import React, { useMemo } from "react";
import styles from "../../../../styles/AdminConsole.module.css";

/**
 * Pagination
 *
 * Props:
 *   currentPage   number
 *   totalPages    number
 *   onPageChange  (page: number) => void
 *
 * Renders nothing when totalPages <= 1.
 * Page number buttons collapse to a sliding window of 7 when
 * totalPages is large, so the bar never overflows on small screens.
 */
const WINDOW = 7; // max page buttons shown at once

const Pagination = React.memo(function Pagination({
  currentPage,
  totalPages,
  onPageChange,
}) {
  const pages = useMemo(() => {
    if (totalPages <= WINDOW) {
      return Array.from({ length: totalPages }, (_, i) => i + 1);
    }
    // Build a window centred on currentPage with ellipsis sentinels (null)
    const half = Math.floor(WINDOW / 2);
    let start = Math.max(2, currentPage - half);
    let end = Math.min(totalPages - 1, currentPage + half);

    // Clamp so we always show WINDOW - 2 middle pages
    if (currentPage - half < 2) end = Math.min(totalPages - 1, WINDOW - 1);
    if (currentPage + half > totalPages - 1)
      start = Math.max(2, totalPages - WINDOW + 2);

    const middle = Array.from({ length: end - start + 1 }, (_, i) => start + i);
    return [
      1,
      ...(start > 2 ? [null] : []),
      ...middle,
      ...(end < totalPages - 1 ? [null] : []),
      totalPages,
    ];
  }, [currentPage, totalPages]);

  if (totalPages <= 1) return null;

  return (
    <div className={styles.pagination}>
      <button
        className={styles.pageBtn}
        disabled={currentPage === 1}
        onClick={() => onPageChange(currentPage - 1)}
      >
        ‹
      </button>

      {pages.map((p, i) =>
        p === null ? (
          <span
            key={`ellipsis-${i}`}
            className={styles.pageBtn}
            style={{ pointerEvents: "none", opacity: 0.4 }}
          >
            …
          </span>
        ) : (
          <button
            key={p}
            className={`${styles.pageBtn} ${p === currentPage ? styles.pageBtnActive : ""}`}
            onClick={() => onPageChange(p)}
          >
            {p}
          </button>
        ),
      )}

      <button
        className={styles.pageBtn}
        disabled={currentPage === totalPages}
        onClick={() => onPageChange(currentPage + 1)}
      >
        ›
      </button>
    </div>
  );
});

export default Pagination;
